<script setup>
import { computed, onBeforeUnmount, ref } from 'vue';
import AbsenceService from '@/api/services/absence.service';
import SupportingDocumentService from '@/api/services/absenceSupportingDocument.service';
import AbsenceDocumentAttachment from '@/components/shared/AbsenceDocumentAttachment.vue';
import AbsenceDocumentPicker from '@/components/shared/AbsenceDocumentPicker.vue';
import SharedDeleteConfirmDialog from '@/components/shared/SharedDeleteConfirmDialog.vue';
import { useAuthStore } from '@/app/stores/authStore';
import { useUiStore } from '@/app/stores/uiStore';
import notification from '@/utils/plugins/NotificationService';
import { absenceApiError, dateInputFormat, toMadridInput, toMadridISO, validateAbsenceRange } from '@/utils/presenceAbsence';

// eslint-disable-next-line no-undef
const props = defineProps({ absence: { type: Object, required: true }, readOnly: Boolean });
// eslint-disable-next-line no-undef
const emit = defineEmits(['saved', 'cancel', 'busy']);
const uiStore = useUiStore();
const authStore = useAuthStore();
const toDate = ref(toMadridInput(props.absence.toDate));
const observations = ref(props.absence.observations || '');
const documents = ref([...(props.absence.absenceSupportingDocuments || [])]);
const pendingFiles = ref([]);
const saving = ref(false);
const error = ref('');
const deleteShown = ref(false);
const removal = ref(null);
const canEdit = computed(() => {
  const employee = props.absence.employee;
  const ownerId = typeof employee === 'string' ? employee.split('/').pop()
    : employee?.id ?? employee?.['@id']?.split('/').pop();
  const currentId = authStore.getUserLogged?.id;
  return !props.readOnly && props.absence.createdFromPresence === true &&
    currentId != null && ownerId != null && String(ownerId) === String(currentId);
});
const duration = computed(() => {
  const value = props.absence.absentWorkTime;
  if (value == null || !Number.isFinite(Number(value))) return '—';
  const minutes = Math.max(0, Number(value));
  return Math.floor(minutes / 60) + ' h ' + String(minutes % 60).padStart(2, '0') + ' min';
});
const iri = document => typeof document === 'string' ? document : document?.['@id'];
const initialDocuments = documents.value.map(iri);
const saved = ref(false);
// Justificantes subidos en esta sesión que aún no están vinculados a la ausencia (el PUT no se ha completado).
const uploadedIds = ref([]);
const closeWarningShown = ref(false);
const hasUnsavedChanges = computed(() => !saved.value && (
  pendingFiles.value.length > 0 ||
  JSON.stringify(documents.value.map(iri)) !== JSON.stringify(initialDocuments) ||
  (canEdit.value && (toDate.value !== toMadridInput(props.absence.toDate) ||
    observations.value !== (props.absence.observations || '')))
));
function requestClose() {
  if (saving.value) return;
  if (hasUnsavedChanges.value) closeWarningShown.value = true;
  else emit('cancel');
}
function discardChanges() {
  if (saving.value) return;
  closeWarningShown.value = false;
  emit('cancel');
}
async function saveAndClose() {
  closeWarningShown.value = false;
  await save();
}
// eslint-disable-next-line no-undef
defineExpose({ requestClose });



function addFiles(event) {
  if (!canEdit.value || saving.value) return;
  const files = Array.from(event.target.files || []);
  event.target.value = '';
  error.value = '';
  if (files.some(file => file.size > 5 * 1024 * 1024 ||
    !/\.(pdf|jpg|jpeg|png|gif|webp|doc|docx)$/i.test(file.name))) {
    error.value = 'Use ficheros de hasta 5 MB: pdf, jpg, jpeg, png, gif, webp, doc o docx.';
    return;
  }
  pendingFiles.value.push(...files);
}
function requestRemoval(index, pending) {
  if (!canEdit.value || saving.value) return;
  removal.value = { index, pending };
  deleteShown.value = true;
}
function discardUploaded(id) {
  uploadedIds.value = uploadedIds.value.filter(value => value !== id);
  return SupportingDocumentService.deleteAbsenceSupportingDocument(id);
}
// Cancelar o cerrar sin guardar no debe dejar ficheros subidos sin ausencia (el servidor los purga a las 24 h).
onBeforeUnmount(() => {
  if (!saved.value) [...uploadedIds.value].forEach(discardUploaded);
});
function removeDocument() {
  if (!canEdit.value || !removal.value || saving.value) return;
  const list = removal.value.pending ? pendingFiles.value : documents.value;
  const [removed] = list.splice(removal.value.index, 1);
  // Subido en esta sesión y sin vincular: se retira ya; uno ya vinculado lo borra el servidor al guardar.
  if (!removal.value.pending && uploadedIds.value.includes(removed?.id)) discardUploaded(removed.id);
  deleteShown.value = false;
  removal.value = null;
}
async function save() {
  if (saving.value || !canEdit.value) return;
  error.value = props.absence.fromDate && toDate.value
    ? validateAbsenceRange(toMadridInput(props.absence.fromDate), toDate.value) : '';
  if (error.value) return;
  saving.value = true;
  emit('busy', true);
  uiStore.startLoading();
  try {
    // Keep successful uploads for a retry if a later upload or PUT fails.
    while (pendingFiles.value.length) {
      const response = await SupportingDocumentService.uploadDocumentFile(pendingFiles.value[0]);
      if (!response?.data?.['@id']) throw new Error('No se ha podido subir el justificante.');
      documents.value.push(response.data);
      if (response.data.id != null) uploadedIds.value.push(response.data.id);
      pendingFiles.value.shift();
    }
    const supportingDocuments = documents.value.map(iri);
    if (supportingDocuments.some(value => !value)) throw new Error('Hay un justificante sin identificador válido.');
    const payload = { absenceSupportingDocuments: supportingDocuments };
    if (canEdit.value) {
      payload.toDate = toDate.value ? toMadridISO(toDate.value) : null;
      payload.observations = observations.value.trim();
    }
    const response = await AbsenceService.updateMyAbsence(payload, props.absence.id);
    if (!(response?.status >= 200 && response.status < 300)) throw new Error('No se ha podido guardar la ausencia.');
    saved.value = true;
    uploadedIds.value = [];
    notification.addNotification({ type: 'success', message: 'Ausencia actualizada correctamente' });
    emit('busy', false);
    emit('saved');
  } catch (failure) {
    error.value = absenceApiError(failure);
  } finally {
    saving.value = false;
    emit('busy', false);
    uiStore.stopLoading();
  }
}
</script>

<template>
  <form @submit.prevent="save" class="own-absence-form">
    <div class="row g-3 mb-3">
      <div class="col-12 col-md-6">
        <span class="absence-field-label">Motivo</span>
        <div class="absence-value">{{ absence.absenceType?.name || '—' }}</div>
      </div>
      <div class="col-12 col-md-6">
        <span class="absence-field-label">Duración</span>
        <div class="absence-value">{{ duration }}</div>
      </div>
      <div class="col-12 col-md-6">
        <span class="absence-field-label">Inicio</span>
        <div class="absence-value">{{ toMadridInput(absence.fromDate) }}</div>
      </div>
      <div class="col-12 col-md-6">
        <div v-if="canEdit" class="form-item date-picker mb-0">
          <label for="own-absence-end" class="w-100">*Hasta</label>
          <el-date-picker id="own-absence-end" v-model="toDate" type="datetime" class="w-100"
            :value-format="dateInputFormat" format="DD/MM/YYYY HH:mm" :clearable="false" :disabled="saving || !canEdit" />
        </div>
        <template v-else>
          <span class="absence-field-label">Fin</span>
          <div class="absence-value">{{ toMadridInput(absence.toDate) }}</div>
        </template>
      </div>
    </div>
    <label v-if="canEdit" for="own-absence-notes" class="w-100">Observaciones</label>
    <span v-else class="absence-field-label">Observaciones</span>
    <el-input v-if="canEdit" id="own-absence-notes" v-model="observations" type="textarea" :rows="5"
      class="w-100 mb-3" :disabled="saving || !canEdit" />
    <div v-else class="absence-value absence-notes mb-3">{{ absence.observations || 'Sin observaciones' }}</div>
    <AbsenceDocumentPicker v-if="canEdit" id="own-absence-files" multiple class="mb-3"
      :disabled="saving" @change="addFiles" />
    <p v-if="!documents.length && !pendingFiles.length" class="absence-value absence-empty">Sin justificantes</p>
    <ul class="attachments">
      <li v-for="(document, index) in documents" :key="iri(document)">
        <AbsenceDocumentAttachment :attachment="document" :disabled="saving" :removable="canEdit"
          @remove="requestRemoval(index, false)" />
      </li>
      <li v-for="(file, index) in pendingFiles" :key="file.name + '-' + file.lastModified + '-' + index">
        <AbsenceDocumentAttachment :attachment="file" file :disabled="saving"
          @remove="requestRemoval(index, true)" />
      </li>
    </ul>
    <p v-if="error" role="alert" class="text-danger">{{ error }}</p>
    <div class="edit-actions d-flex justify-content-end gap-3 mt-4">
      <button type="button" class="btn btn_yellow" :disabled="saving" @click="requestClose">Cerrar</button>
      <button v-if="canEdit" type="submit" class="btn btn_red" :disabled="saving">Guardar</button>
    </div>
    <SharedDeleteConfirmDialog v-model="deleteShown" title="Eliminar justificante"
      message="¿Desea quitar este justificante de la ausencia? El cambio se aplicará al guardar."
      @confirm="removeDocument" @cancel="removal = null" />
    <SharedDeleteConfirmDialog v-model="closeWarningShown" title="Eliminar cambios sin guardar"
      message="Hay archivos y/o cambios que aún no se han guardado."
      secondary-message="¿Quieres subir los archivos y guardar los cambios antes de cerrar?"
      confirm-text="Cerrar sin guardar" cancel-text="Seguir editando" :width="680" :loading="saving"
      :show-title-icon="false" @confirm="discardChanges">
      <template #extra-action="{ busy }">
        <button type="button" class="btn btn_green px-4" :disabled="busy" @click="saveAndClose">Guardar y cerrar</button>
      </template>
    </SharedDeleteConfirmDialog>
  </form>
</template>

<style scoped lang="scss">
.own-absence-form {
  min-width: 0;
  overflow-wrap: anywhere;
  label, .absence-field-label { display: block; color: #434343; font-size: 15px; font-weight: 600; margin-bottom: 8px; }
  :deep(.el-date-editor.el-input) { width: 100%; min-width: 0; height: 44px; align-items: center; }
  :deep(.el-textarea__inner) {  border-radius: 20px; }

  button:disabled { opacity: 0.5; cursor: not-allowed; }
  .attachments { padding-left: 0; list-style: none; }
  .attachments li { margin-bottom: 12px; min-width: 0; }
}
@media (max-width: 767px) {
  .edit-actions { flex-direction: column; }
  .edit-actions button { width: 100%; min-height: 44px; }
}
</style>
